import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import * as TokenService from '@/network/token';
import i18n from '@/i18n';
import type { User } from '../types';

interface OriginalAuth {
  user: User;
  accessToken: string;
  permissions: string[];
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  permissions: string[];
  permissionsLoaded: boolean;
  isAuthenticated: boolean;
  isImpersonating: boolean;
  originalAuth: OriginalAuth | null;
  _hasHydrated: boolean;

  setAuth: (user: User, accessToken: string, permissions?: string[]) => void;
  setUser: (user: User | null) => void;
  setPermissions: (permissions: string[]) => void;
  clearAuth: () => void;
  updateTokens: (accessToken: string) => void;
  startImpersonation: (user: User, accessToken: string, permissions: string[]) => void;
  stopImpersonation: () => void;
  _setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      permissions: [],
      permissionsLoaded: false,
      isAuthenticated: false,
      isImpersonating: false,
      originalAuth: null,
      _hasHydrated: false,

      setAuth: (user: User, accessToken: string, permissions?: string[]) => {
        TokenService.setTokens(accessToken);
        set({
          user,
          accessToken,
          isAuthenticated: true,
          ...(permissions !== undefined && { permissions, permissionsLoaded: true }),
        });
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user && !!get().accessToken });
      },

      setPermissions: (permissions: string[]) => {
        set({ permissions, permissionsLoaded: true });
      },

      clearAuth: () => {
        TokenService.removeTokens();
        set({
          user: null, accessToken: null, permissions: [], permissionsLoaded: false,
          isAuthenticated: false, isImpersonating: false, originalAuth: null,
        });
      },

      updateTokens: (accessToken: string) => {
        TokenService.setTokens(accessToken);
        set({ accessToken, isAuthenticated: true });
      },

      startImpersonation: (user: User, accessToken: string, permissions: string[]) => {
        const state = get();
        const originalAuth: OriginalAuth = {
          user: state.user!,
          accessToken: state.accessToken!,
          permissions: state.permissions,
        };
        TokenService.setTokens(accessToken);
        set({
          originalAuth,
          isImpersonating: true,
          user,
          accessToken,
          permissions,
          permissionsLoaded: true,
          isAuthenticated: true,
        });
      },

      stopImpersonation: () => {
        const { originalAuth } = get();
        if (!originalAuth) {
          // Safety: clear the flag even if originalAuth is missing
          set({ isImpersonating: false, originalAuth: null });
          return;
        }
        TokenService.setTokens(originalAuth.accessToken);
        set({
          user: originalAuth.user,
          accessToken: originalAuth.accessToken,
          permissions: originalAuth.permissions,
          permissionsLoaded: true,
          isAuthenticated: true,
          isImpersonating: false,
          originalAuth: null,
        });
      },

      _setHasHydrated: (state: boolean) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: 'vidnight-auth',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        permissions: state.permissions,
        permissionsLoaded: state.permissionsLoaded,
        // isImpersonating and originalAuth are intentionally NOT persisted.
        // Impersonation is session-only — page refresh ends it and restores the admin.
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state?.accessToken) {
            TokenService.setTokens(state.accessToken);
          }
          // Apply user's saved language preference on rehydration,
          // but only if onboarding is complete (profile fields filled).
          // New users get the DB default 'he' which shouldn't override
          // the language they were using on the login page.
          const user = state?.user;
          const profileComplete = user?.firstNameHe && user?.lastNameHe && user?.phone;
          const lang = user?.preferredLanguage;
          if (profileComplete && lang && lang !== i18n.language) {
            i18n.changeLanguage(lang);
          }
          state?._setHasHydrated(true);
        };
      },
    }
  )
);

export const useAccessToken = () => useAuthStore((s) => s.accessToken);
export const useUser = () => useAuthStore((s) => s.user);
export const usePermissions = () => useAuthStore((s) => s.permissions);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useIsImpersonating = () => useAuthStore((s) => s.isImpersonating);
